import React from 'react';
import { X as XIcon, Filter as FilterIcon } from 'lucide-react';

/**
 * Visual stat card with gradient, shadow and icon-in-circle.
 * Same vocabulary used in Invoices, Subscriptions, Quotes, DeliveryNotes,
 * Expenses and Receivables pages so the app feels coherent.
 */
export const StatCard = ({ label, value, sub, icon: Icon, gradient, iconBg, testid }) => (
  <div
    data-testid={testid}
    className={`relative overflow-hidden rounded-2xl p-5 border border-white/40 shadow-[0_8px_24px_rgb(0,0,0,0.06)] hover:shadow-[0_12px_30px_rgb(0,0,0,0.08)] hover:-translate-y-0.5 transition-all duration-300 ${gradient}`}
  >
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-stone-500 mb-1">{label}</p>
        <p className="text-2xl font-extrabold text-stone-900 tracking-tight font-heading truncate">{value}</p>
        {sub && <p className="text-xs text-stone-500 mt-1">{sub}</p>}
      </div>
      {Icon && (
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${iconBg}`}>
          <Icon className="w-5 h-5 text-white" strokeWidth={2} />
        </div>
      )}
    </div>
  </div>
);

/** Rectangular pill that lifts on active state. */
export const FilterChip = ({ active, onClick, children, testid, dot }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`relative inline-flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-semibold border transition-all duration-200 whitespace-nowrap ${
      active
        ? 'bg-[#FF6B4A] text-white border-[#FF6B4A] shadow-md shadow-orange-200/60 -translate-y-0.5'
        : 'bg-white border-stone-200 text-stone-700 hover:border-orange-300 hover:bg-orange-50/40 shadow-sm'
    }`}
  >
    {dot && <span className={`inline-block w-1.5 h-1.5 rounded-full ${active ? 'bg-white/80' : dot}`} />}
    {children}
  </button>
);

/** Wrapper for a section of filter chips with a uppercase label. */
export const FilterRow = ({ label, children, testid }) => (
  <div className="flex items-center gap-3 flex-wrap">
    <span className="text-[10px] font-bold uppercase tracking-widest text-stone-400 flex items-center gap-1.5">
      <FilterIcon className="w-3 h-3" /> {label}
    </span>
    <div className="flex gap-2 flex-wrap" data-testid={testid}>{children}</div>
  </div>
);

/** Button shown on top-right of the filter card when at least one filter is active. */
export const ResetFiltersButton = ({ count, onClick, testid }) => (
  <button
    onClick={onClick}
    className="inline-flex items-center gap-2 px-4 h-11 rounded-xl text-sm font-semibold text-stone-600 bg-stone-50 hover:bg-stone-100 border border-stone-200 transition-all whitespace-nowrap"
    data-testid={testid || 'reset-filters'}
  >
    <XIcon className="w-3.5 h-3.5" /> Limpiar ({count})
  </button>
);

/** Card wrapper for the filter section. */
export const FiltersCard = ({ children, testid = 'filters-card' }) => (
  <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 p-5 space-y-4" data-testid={testid}>
    {children}
  </div>
);

/** Card wrapper used for tables/lists below the filters. */
export const SurfaceCard = ({ children, testid, className = '' }) => (
  <div
    className={`bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 overflow-hidden ${className}`}
    data-testid={testid}
  >
    {children}
  </div>
);
